/**
 * RevenueCat In-App Purchase Service
 * 
 * Handles Apple IAP subscriptions on native (Capacitor) platforms.
 * Uses STATIC import so the module is included in the build.
 */
import { Purchases } from '@revenuecat/purchases-capacitor';

let initialized = false;

/**
 * Initialize RevenueCat SDK
 */
export const initRevenueCat = async (userId) => {
  if (initialized) return;

  try {
    const apiKey = process.env.REACT_APP_REVENUECAT_KEY || '';
    if (!apiKey) {
      console.warn('[RC] REACT_APP_REVENUECAT_KEY not set');
      return;
    }

    await Purchases.configure({
      apiKey,
      appUserID: userId || undefined
    });

    initialized = true;
    console.log('[RC] RevenueCat initialized for user:', userId);
  } catch (e) {
    console.error('[RC] RevenueCat init error:', e);
  }
};

/**
 * Get available subscription offerings
 */
export const getOfferings = async () => {
  if (!initialized) {
    console.warn('[RC] Not initialized, trying to init...');
    await initRevenueCat();
  }
  if (!initialized) return [];

  try {
    const offerings = await Purchases.getOfferings();
    console.log('[RC] Offerings result:', JSON.stringify(offerings));
    if (!offerings.current?.availablePackages) return [];

    return offerings.current.availablePackages.map(pkg => ({
      identifier: pkg.identifier,
      priceString: pkg.product?.priceString || '',
      price: pkg.product?.price || 0,
      productId: pkg.product?.productIdentifier || '',
      title: pkg.product?.title || '',
      description: pkg.product?.description || '',
      product: pkg.product,
      _raw: pkg
    }));
  } catch (e) {
    console.error('[RC] Error fetching offerings:', e);
    return [];
  }
};

/**
 * Purchase a subscription package
 */
export const purchasePackage = async (pkg) => {
  if (!initialized) {
    return { success: false, tier: null, error: 'RevenueCat not initialized' };
  }

  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg._raw });
    const active = result.customerInfo?.entitlements?.active || {};

    let tier = 'free';
    if (active['club_access'] || active['club'] || active['10 Millions Investor Pro']) tier = 'club';
    if (active['expert_access'] || active['expert']) tier = 'expert';

    return { success: true, tier, error: null };
  } catch (e) {
    if (e.code === 'PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR' ||
        e.message?.includes('cancelled') || e.message?.includes('canceled')) {
      return { success: false, tier: null, error: null };
    }
    console.error('[RC] Purchase error:', e);
    return { success: false, tier: null, error: e.message || 'Erreur lors de l\'achat' };
  }
};

/**
 * Check current subscription status
 */
export const checkSubscriptionStatus = async () => {
  if (!initialized) return { tier: 'free', isActive: false };

  try {
    const info = await Purchases.getCustomerInfo();
    const active = info.customerInfo?.entitlements?.active || {};

    if (active['expert_access'] || active['expert']) return { tier: 'expert', isActive: true };
    if (active['club_access'] || active['club'] || active['10 Millions Investor Pro']) return { tier: 'club', isActive: true };
    return { tier: 'free', isActive: false };
  } catch (e) {
    console.error('[RC] Error checking subscription:', e);
    return { tier: 'free', isActive: false };
  }
};

/**
 * Restore previous purchases
 */
export const restorePurchases = async () => {
  if (!initialized) return { success: false, tier: 'free' };

  try {
    const info = await Purchases.restorePurchases();
    const active = info.customerInfo?.entitlements?.active || {};

    let tier = 'free';
    if (active['club_access'] || active['club'] || active['10 Millions Investor Pro']) tier = 'club';
    if (active['expert_access'] || active['expert']) tier = 'expert';

    return { success: tier !== 'free', tier };
  } catch (e) {
    console.error('[RC] Restore error:', e);
    return { success: false, tier: 'free' };
  }
};
